import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PlayerForm, { type PlayerFormValues } from '../components/players/PlayerForm'
import PhotoUpload from '../components/PhotoUpload'
import { apiFetch, apiJson } from '../lib/api'

interface CreateResponse {
  id: string
  slug: string
}

export default function PlayerNew() {
  const navigate = useNavigate()
  // Deferred photo upload — mirrors b872636 (CoachSetup) and 7a2a129
  // (AgentSetup). The players row doesn't exist yet, so
  // /api/upload/photo with entity_type=player would 404 in
  // requirePlayerOwner. Hold the resized blob locally; POST it after
  // the players row is created on submit. Photo is optional — submit
  // succeeds without one, photo failure is logged and non-blocking.
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null)

  async function submit(values: PlayerFormValues) {
    if (!values.position) throw new Error('position_required')
    // Same parsing pattern as PlayerEdit — empty input → null, non-
    // positive-integer → throw with a friendly message.
    const apiSportsRaw = values.api_sports_player_id.trim()
    let apiSportsParsed: number | null = null
    if (apiSportsRaw !== '') {
      const parsed = Number(apiSportsRaw)
      if (!Number.isInteger(parsed) || parsed <= 0) {
        throw new Error('api_sports_player_id_invalid')
      }
      apiSportsParsed = parsed
    }
    const res = await apiJson<CreateResponse>('/api/players', {
      method: 'POST',
      body: JSON.stringify({
        name: values.name.trim(),
        position: values.position,
        nationality: values.nationality.trim(),
        university: values.university.trim() || null,
        dob: values.dob.trim() || null,
        scouting_narrative: values.scouting_narrative.trim() || null,
        api_sports_player_id: apiSportsParsed,
      }),
    })
    if (photoBlob) {
      try {
        const fd = new FormData()
        fd.append(
          'file',
          new File([photoBlob], 'photo.jpg', { type: 'image/jpeg' }),
        )
        fd.append('entity_type', 'player')
        fd.append('entity_id', res.id)
        const upload = await apiFetch('/api/upload/photo', {
          method: 'POST',
          body: fd,
        })
        if (!upload.ok) {
          console.warn(
            '[player-new] photo upload failed post-create',
            upload.status,
          )
        }
      } catch (uploadErr) {
        console.warn('[player-new] photo upload threw post-create', uploadErr)
      }
    }
    navigate(`/players/${res.slug}`, { replace: true })
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold text-hoop-text mb-1">
        Add a player
      </h1>
      <p className="text-hoop-text-3 text-sm mb-8">
        You can fill in measurements, film, and references later.
      </p>
      <div className="mb-8">
        <PhotoUpload
          entityType="player"
          currentPhotoUrl={null}
          onChange={() => {}}
          deferred
          onFilePicked={setPhotoBlob}
          shape="square"
          label="Player headshot (optional)"
        />
      </div>
      <PlayerForm
        submitLabel="Create player"
        onSubmit={submit}
        onCancel={() => navigate('/dashboard')}
      />
    </div>
  )
}
